import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Eye, 
  EyeOff,
  CheckCircle,
  AlertCircle,
  Star,
  Upload,
  Calendar,
  Mail,
  Phone,
  Hash,
  Sliders,
  Type,
  List,
  CheckSquare,
  ToggleLeft
} from 'lucide-react';
import toast from 'react-hot-toast';
import { formsAPI } from '../utils/api';

const FormView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [showAnswers, setShowAnswers] = useState(false);

  // Fetch form data
  useEffect(() => {
    const fetchForm = async () => {
      try {
        setLoading(true);
        const response = await formsAPI.getForm(id);
        setForm(response.data.data);
        
        // Initialize answers object
        const initialAnswers = {};
        response.data.data.questions.forEach(question => {
          if (question.type === 'multiple-choice') {
            initialAnswers[question.id] = [];
          } else {
            initialAnswers[question.id] = '';
          }
        });
        setAnswers(initialAnswers);
      } catch (error) {
        console.error('Error fetching form:', error);
        toast.error('Failed to load form');
        navigate('/forms');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchForm();
    }
  }, [id, navigate]);

  // Handle answer change
  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Handle multiple choice change
  const handleMultipleChoiceChange = (questionId, optionValue, checked) => {
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || [];
      if (checked) {
        return {
          ...prev,
          [questionId]: [...currentAnswers, optionValue]
        };
      } else {
        return {
          ...prev,
          [questionId]: currentAnswers.filter(val => val !== optionValue)
        };
      }
    });
  };

  // Validate form
  const validateForm = () => {
    const errors = [];
    
    form.questions.forEach(question => {
      if (question.required) {
        const answer = answers[question.id];
        if (!answer || (Array.isArray(answer) && answer.length === 0) || (typeof answer === 'string' && !answer.trim())) {
          errors.push(`${question.question} is required`);
        }
      }
    });
    
    return errors;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    try {
      setSubmitting(true);
      
      const submissionData = {
        answers,
        submissionData: {
          timeSpent: 0, // This would be calculated in a real implementation
          deviceType: 'desktop',
          browser: navigator.userAgent,
          operatingSystem: navigator.platform
        }
      };

      await formsAPI.submitForm(id, submissionData);
      
      toast.success('Form submitted successfully!');
      setShowAnswers(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  // Get question icon
  const getQuestionIcon = (type) => {
    switch (type) {
      case 'text':
      case 'textarea':
        return Type;
      case 'email':
        return Mail;
      case 'phone':
        return Phone;
      case 'number':
        return Hash;
      case 'date':
        return Calendar;
      case 'single-choice':
      case 'multiple-choice':
        return List;
      case 'yes-no':
        return ToggleLeft;
      case 'rating':
        return Star;
      case 'scale':
        return Sliders;
      case 'file-upload':
        return Upload;
      default:
        return Type;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Form not found</h2>
          <button
            onClick={() => navigate('/forms')}
            className="text-primary-600 hover:text-primary-700"
          >
            ← Back to Forms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/forms')}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{form.title}</h1>
                {form.description && (
                  <p className="text-gray-600 mt-1">{form.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowAnswers(!showAnswers)}
                className="inline-flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {showAnswers ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                {showAnswers ? 'Hide Answers' : 'Show Answers'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        <div className="max-w-2xl mx-auto">
          {showAnswers ? (
            /* Show submitted answers */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-8"
            >
              <div className="text-center mb-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Form Submitted Successfully!</h2>
                <p className="text-gray-600">
                  {form.settings.customThankYouMessage || 'Thank you for your submission.'}
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Your Answers</h3>
                {form.questions.map((question, index) => {
                  const answer = answers[question.id];
                  const IconComponent = getQuestionIcon(question.type);
                  
                  return (
                    <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <IconComponent className="w-5 h-5 text-gray-400 mt-1" />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-2">
                            {index + 1}. {question.question}
                            {question.required && <span className="text-red-500 ml-1">*</span>}
                          </h4>
                          <div className="text-gray-600">
                            {question.type === 'multiple-choice' ? (
                              Array.isArray(answer) ? answer.join(', ') : 'No answer'
                            ) : question.type === 'yes-no' ? (
                              answer ? 'Yes' : 'No'
                            ) : question.type === 'rating' ? (
                              `${answer}/5 ⭐`
                            ) : question.type === 'scale' ? (
                              `${answer} (${question.minLabel || question.scaleMin} - ${question.maxLabel || question.scaleMax})`
                            ) : (
                              answer || 'No answer'
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {form.settings.redirectUrl && (
                <div className="mt-8 text-center">
                  <a
                    href={form.settings.redirectUrl}
                    className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Continue
                  </a>
                </div>
              )}
            </motion.div>
          ) : (
            /* Show form for submission */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-8"
            >
              <form onSubmit={handleSubmit} className="space-y-8">
                {form.questions.map((question, index) => {
                  const IconComponent = getQuestionIcon(question.type);
                  
                  return (
                    <div key={question.id} className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <IconComponent className="w-5 h-5 text-gray-400 mt-1" />
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-900">
                            {index + 1}. {question.question}
                            {question.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                        </div>
                      </div>

                      {/* Question Input */}
                      {question.type === 'text' && (
                        <input
                          type="text"
                          value={answers[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          placeholder={question.placeholder}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      )}

                      {question.type === 'textarea' && (
                        <textarea
                          value={answers[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          placeholder={question.placeholder}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      )}

                      {question.type === 'email' && (
                        <input
                          type="email"
                          value={answers[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          placeholder={question.placeholder}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      )}

                      {question.type === 'phone' && (
                        <input
                          type="tel"
                          value={answers[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          placeholder={question.placeholder}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      )}

                      {question.type === 'number' && (
                        <input
                          type="number"
                          value={answers[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          min={question.min}
                          max={question.max}
                          step={question.step}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      )}

                      {question.type === 'date' && (
                        <input
                          type="date"
                          value={answers[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      )}

                      {question.type === 'single-choice' && (
                        <div className="space-y-2">
                          {question.options.map((option) => (
                            <label key={option.id} className="flex items-center">
                              <input
                                type="radio"
                                name={`question-${question.id}`}
                                value={option.value}
                                checked={answers[question.id] === option.value}
                                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {question.type === 'multiple-choice' && (
                        <div className="space-y-2">
                          {question.options.map((option) => (
                            <label key={option.id} className="flex items-center">
                              <input
                                type="checkbox"
                                value={option.value}
                                checked={(answers[question.id] || []).includes(option.value)}
                                onChange={(e) => handleMultipleChoiceChange(question.id, option.value, e.target.checked)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {question.type === 'yes-no' && (
                        <div className="flex space-x-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`question-${question.id}`}
                              value="yes"
                              checked={answers[question.id] === 'yes'}
                              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                              className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Yes</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`question-${question.id}`}
                              value="no"
                              checked={answers[question.id] === 'no'}
                              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                              className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">No</span>
                          </label>
                        </div>
                      )}

                      {question.type === 'rating' && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">{question.minLabel || question.scaleMin}</span>
                          <div className="flex space-x-1">
                            {Array.from({ length: question.scaleMax - question.scaleMin + 1 }, (_, i) => {
                              const rating = question.scaleMin + i;
                              return (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => handleAnswerChange(question.id, rating)}
                                  className={`w-8 h-8 focus:outline-none ${
                                    (answers[question.id] || 0) >= rating
                                      ? 'text-yellow-400'
                                      : 'text-gray-300 hover:text-yellow-300'
                                  }`}
                                >
                                  <Star className="w-6 h-6" />
                                </button>
                              );
                            })}
                          </div>
                          <span className="text-sm text-gray-600">{question.maxLabel || question.scaleMax}</span>
                        </div>
                      )}

                      {question.type === 'scale' && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm text-gray-600">
                            <span>{question.minLabel || question.scaleMin}</span>
                            <span>{question.maxLabel || question.scaleMax}</span>
                          </div>
                          <input
                            type="range"
                            min={question.scaleMin}
                            max={question.scaleMax}
                            value={answers[question.id] || question.scaleMin}
                            onChange={(e) => handleAnswerChange(question.id, parseInt(e.target.value))}
                            className="w-full"
                          />
                          <div className="text-center text-sm text-gray-600">
                            {answers[question.id] || question.scaleMin}
                          </div>
                        </div>
                      )}

                      {question.type === 'file-upload' && (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">
                            Click to upload or drag and drop
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {question.fileTypes.length > 0 && `Allowed: ${question.fileTypes.join(', ')}`}
                            {question.maxFileSize && ` • Max size: ${question.maxFileSize}MB`}
                          </p>
                          <input
                            type="file"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handleAnswerChange(question.id, {
                                  fileName: file.name,
                                  fileSize: file.size,
                                  file: file
                                });
                              }
                            }}
                            accept={question.fileTypes.map(type => `.${type}`).join(',')}
                            className="hidden"
                            id={`file-${question.id}`}
                          />
                          <label
                            htmlFor={`file-${question.id}`}
                            className="mt-2 inline-block px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer"
                          >
                            Choose File
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? 'Submitting...' : 'Submit Form'}
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormView;


